import { CSVLink } from 'react-csv';
import {
  IconDownload,
  IconExportCSV,
  IconExportExcel,
  IconExportSheet,
  IconExportPDF,
} from '../../../components/Icons';

import './KSExport.scss';
import { processCSV } from './ksCSV';
import { ReportViewModel } from 'types/report';
import parseReportNT from './reportNTParser';
import { exportParser } from './ksExporterUtils';

import { exportToExcel } from './excel/excel';
import { generatePDFPreview } from './pdf/pdf';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ExportData } from './types';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useUserLocationsQuery } from 'hooks/useUserLocationsQuery';
import { useReportsStore } from 'hooks/useReportsStore';
import { shallow } from 'zustand/shallow';
import { logger } from './reportNTParser/utils';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import { AskRequest, ReportTimeframe } from '@hone-automation/common';
import { useLocation } from 'react-router-dom';
import PDFExportSettings, { PDFExportOptions } from './PDFExportSettings';

type KSExportProps = {
  report?: NestedHoneReport | ReportViewModel;
};

function getConsolidatedLocationNames(
  consolidated: boolean | undefined,
  compareLocationsParam: string | null,
  userLocations: HoneLocationUser[] | undefined
): string[] | undefined {
  if (!consolidated || !compareLocationsParam || !userLocations) return undefined;

  return compareLocationsParam
    .split(',')
    .map(id => userLocations.find(loc => loc.locationId === id)?.locationName)
    .filter((name): name is string => !!name);
}

const KSExport = ({ report }: KSExportProps) => {
  if (!report) {
    return null;
  }

  const currentLocation = useLocationsStore(state => state.currentLocation);
  const { data: userLocations } = useUserLocationsQuery();
  const { data: flaggedTransactions } = useQueueRequestsQuery();
  const launchDarklyFlags = useFlags();
  const location = useLocation();
  const trace = false;
  const pdf = true;
  const ref = useRef<HTMLDivElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [data, setData] = useState<ReportViewModel | null>(null);
  const [exportObject, setExportObject] = useState<ExportData | null>(null);
  const [csvObject, setCsvObject] = useState<any>(null);
  const [urlParams, setUrlParams] = useState<URLSearchParams>(new URLSearchParams(location.search));
  const [reversedReport, setReversedReport] = useState<boolean>(false);
  const [consolidated, setConsolidated] = useState<boolean>(false);
  const [pdfSettingsOpen, setPdfSettingsOpen] = useState<boolean>(false);

  const { setEnableAIReport } = useReportsStore();

  const { reportsConfiguration } = useReportsStore(
    state => ({
      reportsConfiguration: state.reportsConfiguration,
    }),
    shallow
  );

  // Track URL parameter changes
  useEffect(() => {
    setUrlParams(new URLSearchParams(location.search));
  }, [location.search]);

  useEffect(() => {
    setReversedReport(urlParams.get('reverse') === 'true');
    setConsolidated(urlParams.get('consolidated') === 'true');
  }, [urlParams]);

  useEffect(() => {
    if ('sections' in report) {
      const parsedData = parseReportNT({
        report,
        useComputedColumns: true,
        periodReporting: reportsConfiguration?.data?.periodReporting,
        reportCFG: reportsConfiguration,
        flaggedTransactions: flaggedTransactions as AskRequest[],
        currentLocation,
        trace,
        reversedReport,
        consolidated,
        extendedAnalysis: true,
        requestedTimeframe: (urlParams?.get('timeframe') ?? undefined) as ReportTimeframe,
        launchDarklyFlags, // Pass LaunchDarkly flags for feature flag control
      });
      setData(parsedData);
      // Use analysis.aiInsights presence to determine if AI report is enabled
      if (parsedData?.analysis?.aiInsights !== undefined) {
        setEnableAIReport(true);
      } else {
        setEnableAIReport(false);
      }
    } else {
      setData(report);
    }
  }, [
    report,
    flaggedTransactions,
    reportsConfiguration,
    launchDarklyFlags, // Add to dependencies
    currentLocation,
    reversedReport,
    consolidated,
    setEnableAIReport,
  ]);

  // Closing the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const prefix = prefixClass('ks-export');

  // Excel Export
  const handleExcelExport = () => {
    if (exportObject) {
      exportToExcel(exportObject);
      setDropdownOpen(false);
    }
  };

  // Sheets Export
  const handleSheetExport = () => {
    if (exportObject) {
      exportToExcel(exportObject, true);
      setDropdownOpen(false);
    }
  };

  // PDF Export - Open settings modal
  const handlePDFExport = () => {
    if (exportObject) {
      setDropdownOpen(false);
      setPdfSettingsOpen(true);
    }
  };

  // Handle PDF export with options - generates PDF and triggers download
  const handlePDFExportWithOptions = (options: PDFExportOptions) => {
    if (exportObject) {
      const dataUrl = generatePDFPreview(exportObject, options);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${exportObject.fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setPdfSettingsOpen(false);
    }
  };

  // Prepare exporter
  const handleExport = () => {
    // Get URL params to configure export - read fresh from current URL to ensure latest values
    const freshUrlParams = new URLSearchParams(window.location.search);
    const percentages = freshUrlParams.get('percentages') !== 'false' && freshUrlParams.get('percentage') !== 'false';
    const money = freshUrlParams.get('amounts') !== 'false' && freshUrlParams.get('money') !== 'false';
    const smoothing = freshUrlParams.get('smoothing') === 'true';
    const actuals = freshUrlParams.get('actuals') !== 'false';
    const variance = freshUrlParams.get('variance') === 'true';
    const budget = freshUrlParams.get('budget') === 'true';
    const transactions = freshUrlParams.get('transactions') === 'true';
    const anomalies = freshUrlParams.get('anomalies') === 'true';
    const total = freshUrlParams.get('total') === 'true';
    const difference = freshUrlParams.get('difference') === 'true';
    const reverse = freshUrlParams.get('reverse') === 'true';

    // Reprocess data with current URL parameters to ensure we have the latest transformations
    let currentData = data;
    if ('sections' in report) {
      currentData = parseReportNT({
        report,
        useComputedColumns: true,
        periodReporting: reportsConfiguration?.data?.periodReporting,
        reportCFG: reportsConfiguration,
        flaggedTransactions: flaggedTransactions as AskRequest[],
        currentLocation,
        trace,
        //reversedReport: reverse,
        consolidated: freshUrlParams.get('consolidated') === 'true',
      });
    }

    const filters = {
      money,
      actuals,
      variance,
      percentage: percentages,
      budget,
      smoothing,
      transactions,
      anomalies,
      total,
      difference,
      reverse,
    };

    // Get report title from the original report if available
    const reportTitle = 'sections' in report ? report.title : undefined;

    // Get location names for consolidated reports from compareLocations URL param
    const compareLocationsParam = urlParams.get('compareLocations');
    const consolidatedLocationNames = getConsolidatedLocationNames(consolidated, compareLocationsParam, userLocations);

    const newExportObject = exportParser({
      transformedData: currentData,
      filters,
      respectCollapsedRows: true,
      trace,
      title: reportTitle,
      locationName: currentLocation?.name,
      isConsolidated: consolidated,
      consolidatedLocationNames,
    });
    if (newExportObject) {
      setExportObject(newExportObject);
      const newCsvObject = processCSV(newExportObject, filters);
      setCsvObject(newCsvObject);
      if (trace) {
        logger.msg('exportObject', newExportObject);
        logger.msg('CSV', newCsvObject);
      }
      setDropdownOpen(true);
    }
  };

  // Calculate data column count for PDF settings
  const getDataColumnCount = (): number => {
    if (!data || !data.data || data.data.length === 0) return 0;
    const firstRow = data.data[0];
    const metadataKeys = ['GL', 'accounts', 'percentOf', 'style', 'budgetAvailable'];
    return Object.keys(firstRow || {}).filter(key => !metadataKeys.includes(key)).length;
  };

  return (
    <>
      <div className={prefix()}>
        <div className={classNames(prefix('icon'))} onClick={handleExport}>
          <IconDownload />
        </div>
        <div className={classNames(prefix('dropdown'), { hidden: !dropdownOpen })} ref={ref}>
          {csvObject && exportObject && (
            <CSVLink
              className={prefix('dropdown-item')}
              data={csvObject.data}
              filename={`${exportObject.fileName}.csv`}
              onClick={() => setDropdownOpen(false)}
            >
              <div className={classNames(prefix('dropdown-item-icon'))}>
                <IconExportCSV />
              </div>
              CSV
            </CSVLink>
          )}
          <div className={prefix('dropdown-item')} onClick={handleSheetExport}>
            <div className={classNames(prefix('dropdown-item-icon'), 'small')}>
              <IconExportSheet />
            </div>
            Google Sheets
          </div>
          <div className={prefix('dropdown-item')} onClick={handleExcelExport}>
            <div className={classNames(prefix('dropdown-item-icon'), 'small')}>
              <IconExportExcel />
            </div>
            Excel
          </div>
          {pdf && (
            <div className={prefix('dropdown-item')} onClick={handlePDFExport}>
              <div className={classNames(prefix('dropdown-item-icon'))}>
                <IconExportPDF />
              </div>
              PDF
            </div>
          )}
        </div>
      </div>

      <PDFExportSettings
        isOpen={pdfSettingsOpen}
        onClose={() => setPdfSettingsOpen(false)}
        onSavePDF={handlePDFExportWithOptions}
        exportData={exportObject}
        defaultColumnCount={getDataColumnCount()}
      />
    </>
  );
};
export default KSExport;
